import React, { useState } from "react";
import { Chart } from "react-google-charts";
import { Card } from "primereact/card";
import { Dropdown } from "primereact/dropdown";
import { Calendar } from "primereact/calendar";
import Spinner from "../../Shared/Spinner";
import { useLinkedInCardsDashboardStats, useLinkedInCardsFilterOptions } from "../../../hooks/use-query-linkedincards";

const LinkedinCardsDashboard: React.FC = () => {
  const [selectedCountry, setSelectedCountry] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<any>(null);

  const filters = {
    country: selectedCountry || undefined,
    startDate: dateRange && dateRange[0] ? dateRange[0].toISOString() : undefined,
    endDate: dateRange && dateRange[1] ? dateRange[1].toISOString() : undefined,
  };

  const { data: statsRes, isLoading } = useLinkedInCardsDashboardStats(filters);
  const { data: filterOptionsRes } = useLinkedInCardsFilterOptions();

  if (isLoading) return <Spinner />;

  const stats = statsRes?.data;
  const countries = filterOptionsRes?.data?.countries || [];
  const countryOptions = [
    { label: "Todos os Países", value: null },
    ...countries.map((c: string) => ({ label: c, value: c })),
  ];

  if (!stats) {
    return (
      <div className="card text-center p-5">
        <h4>Sem estatísticas disponíveis para análise.</h4>
      </div>
    );
  }

  const {
    topCompanies,
    topLocations,
    trends,
    topTitles,
    kpis,
  } = stats;

  // Formatar dados para o Google Charts
  const chartCompaniesData = [
    ["Empresa", "Vagas"],
    ...topCompanies.map((c: any) => [c.companyName, c.count]),
  ];

  const chartTitlesData = [
    ["Cargo", "Vagas"],
    ...topTitles.map((t: any) => [t.title, t.count]),
  ];

  const chartLocationsData = [
    ["Localização", "Vagas"],
    ...topLocations.map((l: any) => [`${l.companyLocation || "N/A"}`, l.count]),
  ];

  const months = [
    "Jan",
    "Fev",
    "Mar",
    "Abr",
    "Mai",
    "Jun",
    "Jul",
    "Ago",
    "Set",
    "Out",
    "Nov",
    "Dez",
  ];
  const chartTrendsData = [
    ["Mês", "Vagas"],
    ...trends.map((t: any) => [`${months[t.month - 1]} ${t.year}`, t.count]),
  ];

  // Opções globais de estilo para o Google Charts (estética premium)
  const chartOptions = {
    backgroundColor: "transparent",
    legend: {
      position: "right",
      textStyle: { fontSize: 12, color: "#4B5563" },
    },
    colors: [
      "#2563EB",
      "#10B981",
      "#F59E0B",
      "#EF4444",
      "#8B5CF6",
      "#EC4899",
      "#3B82F6",
      "#14B8A6",
    ],
    fontName: "Inter, sans-serif",
    chartArea: { width: "60%", height: "70%" },
  };

  const lineChartOptions = {
    ...chartOptions,
    hAxis: { title: "Período", textStyle: { color: "#4B5563" } },
    vAxis: { title: "Quantidade de Vagas", textStyle: { color: "#4B5563" } },
    legend: { position: "none" },
    chartArea: { width: "90%", height: "70%" },
  };

  const barChartOptions = {
    ...chartOptions,
    hAxis: { title: "Vagas", textStyle: { color: "#4B5563" } },
    legend: { position: "none" },
    chartArea: { width: "60%", height: "70%" },
  };

  return (
    <div className="grid p-3 w-full">
      {/* Filtros de País e Intervalo de Datas */}
      <div className="col-12 mb-3">
        <div className="card shadow-1 p-3 bg-white border-round grid grid-nogutter align-items-center">
          <div className="col-12 md:col-6 lg:col-4 p-2 flex flex-column gap-2">
            <label className="font-bold text-700">País</label>
            <Dropdown
              value={selectedCountry}
              options={countryOptions}
              onChange={(e) => setSelectedCountry(e.value)}
              placeholder="Filtrar por País"
              className="w-full"
              showClear
            />
          </div>
          <div className="col-12 md:col-6 lg:col-4 p-2 flex flex-column gap-2">
            <label className="font-bold text-700">Intervalo de Datas</label>
            <Calendar
              value={dateRange}
              onChange={(e) => setDateRange(e.value)}
              selectionMode="range"
              readOnlyInput
              placeholder="Selecionar Período"
              className="w-full"
              showIcon
              showButtonBar
            />
          </div>
        </div>
      </div>

      {/* 1. KPIs Linha de Sumário */}
      <div className="col-12 grid grid-nogutter mb-3">
        <div className="col-12 md:col-4 p-2">
          <div className="card shadow-1 p-3 flex align-items-center border-left-3 border-blue-600 bg-white h-full border-round">
            <i className="pi pi-briefcase text-blue-600 text-3xl mr-3" />
            <div>
              <span className="block text-500 font-bold mb-1">
                Total de Vagas
              </span>
              <span className="text-2xl font-bold text-900">
                {kpis.totalJobs}
              </span>
            </div>
          </div>
        </div>

        <div className="col-12 md:col-4 p-2">
          <div className="card shadow-1 p-3 flex align-items-center border-left-3 border-green-600 bg-white h-full border-round">
            <i className="pi pi-building text-green-600 text-3xl mr-3" />
            <div>
              <span className="block text-500 font-bold mb-1">
                Empresas Monitoradas
              </span>
              <span className="text-2xl font-bold text-900">
                {kpis.totalCompanies}
              </span>
            </div>
          </div>
        </div>

        <div className="col-12 md:col-4 p-2">
          <div className="card shadow-1 p-3 flex align-items-center border-left-3 border-orange-600 bg-white h-full border-round">
            <i className="pi pi-search text-orange-600 text-3xl mr-3" />
            <div>
              <span className="block text-500 font-bold mb-1">
                Pesquisas Ativas
              </span>
              <span className="text-2xl font-bold text-900">
                {kpis.activeRequests}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* 2. Top Empresas */}
      <div className="col-12">
        <Card title="Top 10 Empresas Contratantes">
          {topCompanies.length > 0 ? (
            <Chart
              chartType="BarChart"
              width="100%"
              height="300px"
              data={chartCompaniesData}
              options={barChartOptions}
            />
          ) : (
            <div className="text-center p-5 text-500">
              Sem dados para exibir.
            </div>
          )}
        </Card>
      </div>

      {/* 3. Top Cargos & Geografias mais procuradas */}
      <div className="col-12 md:col-6">
        <Card title="Top 10 Cargos mais Procurados">
          {topTitles.length > 0 ? (
            <Chart
              chartType="BarChart"
              width="100%"
              height="300px"
              data={chartTitlesData}
              options={{ ...chartOptions, legend: { position: "none" } }}
            />
          ) : (
            <div className="text-center p-5 text-500">
              Sem dados para exibir.
            </div>
          )}
        </Card>
      </div>

      <div className="col-12 md:col-6">
        <Card title="Cidades / Regiões com mais Vagas">
          {topLocations.length > 0 ? (
            <Chart
              chartType="BarChart"
              width="100%"
              height="300px"
              data={chartLocationsData}
              options={barChartOptions}
            />
          ) : (
            <div className="text-center p-5 text-500">
              Sem dados para exibir.
            </div>
          )}
        </Card>
      </div>

      {/* 4. Tendências Temporais */}
      <div className="col-12">
        <Card title="Evolução Temporal de Captação de Vagas">
          {trends.length > 0 ? (
            <Chart
              chartType="LineChart"
              width="100%"
              height="350px"
              data={chartTrendsData}
              options={lineChartOptions}
            />
          ) : (
            <div className="text-center p-5 text-500">
              Sem dados de tendências históricas.
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default LinkedinCardsDashboard;
